import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventResponse, TrainingPackageResponse, UserSearchResult, HorseResponse, TrainingRegistrationResponse} from '../../../../../../../../../../@shared/models/response.module';
import {TrainingService} from '../../../../../../../../../../@shared/services/training.service';
import {ToastService} from '../../../../../../../../../../@core/services/toast.service';
import {EndpointService} from '../../../../../../../../../../@shared/endpoints.service';
import {Horses} from '../../../../../../../../../../@shared/services/horses';
import {AddHorse} from '../../../../../../../../../../pages/user-profile/components/user-profile-horses/components/add-horse/add-horse';
import {debounceTime, distinctUntilChanged, switchMap, Subject, forkJoin} from 'rxjs';
import {AQH_HOR_0} from '../../../../../../../../../../@shared/models/input.module';

@Component({
  selector: 'app-add-package-registration',
  standalone: false,
  templateUrl: './add-package-registration.html',
  styleUrl: './add-package-registration.css'
})
export class AddPackageRegistration implements OnInit {
  @Input() package!: TrainingPackageResponse;
  @Input() event!: EventResponse;

  // Member type selection
  memberType: 'existing' | 'manual' = 'existing';

  // User search
  userSearchQuery = '';
  userSearchResults: UserSearchResult[] = [];
  selectedUser: UserSearchResult | null = null;
  isSearchingUsers = false;
  private searchSubject = new Subject<string>();

  // Manual user entry
  firstName = '';
  lastName = '';
  email = '';
  phone = '';

  // Horse selection
  availableHorses: HorseResponse[] = [];
  selectedHorse: HorseResponse | null = null;
  loadingHorses = false;

  // Notes
  notes = '';

  // Payment options
  paymentType: 'manual' | 'send_link' = 'manual';
  paymentStatus: 'not_required' | 'pay_later' | 'paid' = 'paid';
  paymentMethod: 'cash' | 'bank_transfer' | 'other' = 'cash';

  // Submission
  submitting = false;
  errorMessage = '';

  constructor(
    public activeModal: NgbActiveModal,
    private trainingService: TrainingService,
    private endpointService: EndpointService,
    private horsesService: Horses,
    private toastService: ToastService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    // Setup debounced user search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 2) {
          return [];
        }
        this.isSearchingUsers = true;
        return this.endpointService.aqh_eve_67({ search: query });
      })
    ).subscribe({
      next: (response: any) => {
        this.isSearchingUsers = false;
        this.userSearchResults = response?.data || [];
      },
      error: () => {
        this.isSearchingUsers = false;
        this.userSearchResults = [];
      }
    });

    // Load all horses
    this.loadAllHorses();
  }

  loadAllHorses() {
    this.loadingHorses = true;
    const payload: AQH_HOR_0 = {};

    this.horsesService.getHorses(payload).subscribe({
      next: (horses: HorseResponse[]) => {
        this.availableHorses = horses || [];
        this.loadingHorses = false;
      },
      error: () => {
        this.loadingHorses = false;
        this.availableHorses = [];
      }
    });
  }

  onMemberTypeChange() {
    // Reset fields when switching types
    this.selectedUser = null;
    this.userSearchQuery = '';
    this.userSearchResults = [];
    this.firstName = '';
    this.lastName = '';
    this.email = '';
    this.phone = '';
    this.errorMessage = '';
  }

  onUserSearch() {
    this.searchSubject.next(this.userSearchQuery);
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.userSearchQuery = user.full_name || `${user.first_name} ${user.last_name}`;
    this.userSearchResults = [];
  }

  clearSelectedUser() {
    this.selectedUser = null;
    this.userSearchQuery = '';
  }

  onHorseChanged(horseId: any) {
    this.selectedHorse = this.availableHorses.find(h => h.horse_id === horseId) || null;
  }

  openAddHorseModal() {
    const modalRef = this.modalService.open(AddHorse, { size: 'lg', centered: true });

    modalRef.result.then((newHorse: HorseResponse) => {
      if (newHorse) {
        this.toastService.success('Horse added successfully');
        this.loadAllHorses();
      }
    }, () => {});
  }

  close() {
    this.activeModal.dismiss();
  }

  isValid(): boolean {
    if (this.memberType === 'existing') {
      return this.selectedUser !== null;
    } else {
      return this.firstName.trim().length > 0 && this.lastName.trim().length > 0;
    }
  }

  submit() {
    if (!this.isValid()) {
      return;
    }

    // For send_link, we need email
    if (this.paymentType === 'send_link') {
      const userEmail = this.memberType === 'existing' ? this.selectedUser?.email : this.email;
      if (!userEmail) {
        this.errorMessage = 'Email is required to send payment link';
        return;
      }
    }

    this.submitting = true;
    this.errorMessage = '';

    const payload: any = {
      event_id: this.event.event_id,
      package_id: this.package.package_id,
      horse_id: this.selectedHorse?.horse_id || undefined,
      notes: this.notes || undefined,
      // Payment options
      payment_type: this.paymentType,
      // Use 'pay_later' for send_link (confirmed but awaiting payment)
      payment_status: this.paymentType === 'manual' ? this.paymentStatus : 'pay_later',
      payment_method: this.paymentType === 'manual' ? this.paymentMethod : undefined,
      send_payment_link: this.paymentType === 'send_link'
    };

    if (this.memberType === 'existing' && this.selectedUser) {
      payload.user_id = this.selectedUser.user_id;
    } else {
      // Manual entry - include user details for backend to create/find user
      payload.first_name = this.firstName.trim();
      payload.last_name = this.lastName.trim();
      payload.email = this.email.trim() || undefined;
      payload.phone = this.phone.trim() || undefined;
    }

    this.trainingService.createRegistration(payload).subscribe({
      next: (registration: TrainingRegistrationResponse) => {
        // After creating registration, add to existing startlist sessions for this package
        this.addToExistingStartlists(registration.registration_id).then(() => {
          if (this.paymentType === 'send_link') {
            this.toastService.success('Registration added and payment link sent to email');
          } else {
            this.toastService.success('Registration added successfully');
          }
          this.activeModal.close(true);
        });
      },
      error: (err) => {
        this.submitting = false;
        this.errorMessage = err.error?.reason || 'Failed to add registration';
      }
    });
  }

  /**
   * Find all startlist sessions for this package and add the new registration to them
   */
  private async addToExistingStartlists(registrationId: number): Promise<void> {
    try {
      // Fetch all sessions for this event
      const sessions = await this.trainingService.getSessions({ event_id: this.event.event_id }).toPromise();
      if (!sessions) return;

      // Find startlist sessions that use this package
      const startlistSessions = sessions.filter(
        s => s.session_type === 'startlist' && s.package_id === this.package.package_id
      );

      if (startlistSessions.length === 0) return;

      // Add registration to each startlist session
      const bulkAssignCalls = startlistSessions.map(session =>
        this.trainingService.bulkAssign({
          session_id: session.session_id,
          registration_ids: [registrationId]
        })
      );

      // Execute all bulk assign calls
      if (bulkAssignCalls.length > 0) {
        await forkJoin(bulkAssignCalls).toPromise();
      }
    } catch (error) {
      console.error('Error adding registration to startlist sessions:', error);
      // Don't fail the registration creation if this fails
    }
  }

  formatPrice(priceCents: number, currency: string): string {
    return `${(priceCents / 100).toFixed(2)} ${currency}`;
  }
}
